import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import ProtectedShell from "./components/ProtectedShell";
import ScrollToTop from "./components/ScrollToTop";
import RequireRole from "./components/RequireRole";
import RequireSenderCompany from "./components/RequireSenderCompany";
import Login from "./pages/Login";
import StaffLogin from "./pages/StaffLogin";
import ForgotPassword from "./pages/ForgotPassword";
import SetPassword from "./pages/SetPassword";
import SetNewPassword from "./pages/SetNewPassword";
import AccountSecurity from "./pages/AccountSecurity";
import TrackShipment from "./pages/TrackShipment";
import AdminDashboard from "./pages/admin/Dashboard";
import ShipmentDetail from "./pages/admin/ShipmentDetail";
import Users from "./pages/admin/Users";
import Leads from "./pages/admin/Leads";
import ReviewRequests from "./pages/admin/ReviewRequests";
import AirportsRoutes from "./pages/admin/AirportsRoutes";
import CargoAcceptanceSettings from "./pages/admin/CargoAcceptanceSettings";
import EmailHealth from "./pages/admin/EmailHealth";
import Clients from "./pages/admin/Clients";
import ClientShipments from "./pages/admin/ClientShipments";
import PricingSettings from "./pages/admin/PricingSettings";
import PromoCodes from "./pages/admin/PromoCodes";
import LegalAdmin from "./pages/admin/LegalAdmin";
import SenderDashboard from "./pages/sender/Dashboard";
import NewBooking from "./pages/sender/NewBooking";
import ConciergeHome from "./pages/sender/ConciergeHome";
import ShipmentByCode from "./pages/sender/ShipmentByCode";
import ReviseBooking from "./pages/sender/ReviseBooking";
import CompanyDetails from "./pages/sender/CompanyDetails";
import CaptainHome from "./pages/captain/CaptainHome";
import RiderHome from "./pages/rider/RiderHome";
import AccountsHome from "./pages/accounts/AccountsHome";
import AccountsWorkspace from "./pages/accounts/AccountsWorkspace";
import { ConciergeStaffPage } from "./components/concierge/Concierge";
import ScanPackage from "./pages/ScanPackage";
import CompanyOnboarding from "./pages/onboarding/CompanyOnboarding";
import Terms from "./pages/public/Terms";
import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import CancellationRefundPolicy from "./pages/public/CancellationRefundPolicy";
import ShippingServicePolicy from "./pages/public/ShippingServicePolicy";
import Pricing from "./pages/public/Pricing";
import Contact from "./pages/public/Contact";
import PriorityRoutes from "./pages/public/PriorityRoutes";

function RoleRouter() {
  const { profile, loading } = useAuth();
  if (loading) return <div className="p-10 mono text-sm">Loading…</div>;
  if (!profile) return <div className="p-10 mono text-sm">Provisioning profile…</div>;
  if (profile.role === "SENDER")
    return <Navigate to={profile.onboarding_required ? "/onboarding" : "/sender"} replace />;
  if (profile.role === "CAP")
    return <Navigate to="/captain" replace />;
  if (profile.role === "RID") return <Navigate to="/rider" replace />;
  if (profile.role === "ACCOUNTS") return <Navigate to="/accounts" replace />;
  return <AdminDashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-right" closeButton richColors />
          <Routes>
            {/* Public unauthenticated surface */}
            <Route path="/" element={<Login />} />
            <Route path="/interim" element={<Login interim />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/set-new-password" element={<SetNewPassword />} />
            <Route path="/track-my-shipment" element={<TrackShipment />} />
            <Route path="/priority-routes" element={<PriorityRoutes />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
            <Route path="/shipping-service-policy" element={<ShippingServicePolicy />} />

            {/* Authenticated app */}
            <Route element={<ProtectedShell />}>
              <Route path="/app" element={<RoleRouter />} />
              <Route path="/account/security" element={<AccountSecurity />} />

              <Route path="/admin/shipments/:id" element={
                <RequireRole allow={["SUPER_ADMIN","CTRL","CTOP","CAP","RID"]}><ShipmentDetail /></RequireRole>
              } />
              <Route path="/admin/users" element={
                <RequireRole allow={["SUPER_ADMIN"]}><Users /></RequireRole>
              } />
              <Route path="/admin/leads" element={
                <RequireRole allow={["SUPER_ADMIN"]}><Leads /></RequireRole>
              } />
              <Route path="/admin/review-requests" element={
                <RequireRole allow={["SUPER_ADMIN"]}><ReviewRequests /></RequireRole>
              } />
              <Route path="/admin/airports-routes" element={
                <RequireRole allow={["SUPER_ADMIN"]}><AirportsRoutes /></RequireRole>
              } />
              <Route path="/admin/cargo-acceptance" element={
                <RequireRole allow={["SUPER_ADMIN"]}><CargoAcceptanceSettings /></RequireRole>
              } />
              <Route path="/admin/email-delivery" element={
                <RequireRole allow={["SUPER_ADMIN"]}><EmailHealth /></RequireRole>
              } />
              <Route path="/admin/clients" element={
                <RequireRole allow={["SUPER_ADMIN"]}><Clients /></RequireRole>
              } />
              <Route path="/admin/clients/:companyId" element={
                <RequireRole allow={["SUPER_ADMIN"]}><ClientShipments /></RequireRole>
              } />
              <Route path="/admin/pricing-settings" element={
                <RequireRole allow={["SUPER_ADMIN"]}><PricingSettings /></RequireRole>
              } />
              <Route path="/admin/promo-codes" element={
                <RequireRole allow={["SUPER_ADMIN"]}><PromoCodes /></RequireRole>
              } />
              <Route path="/admin/legal" element={
                <RequireRole allow={["SUPER_ADMIN"]}><LegalAdmin /></RequireRole>
              } />

              <Route path="/onboarding" element={
                <RequireRole allow={["SENDER"]}><CompanyOnboarding /></RequireRole>
              } />

              <Route path="/sender" element={
                <RequireRole allow={["SENDER"]}><RequireSenderCompany allowInactive><SenderDashboard /></RequireSenderCompany></RequireRole>
              } />
              <Route path="/sender/company" element={
                <RequireRole allow={["SENDER"]}><RequireSenderCompany><CompanyDetails /></RequireSenderCompany></RequireRole>
              } />
              <Route path="/sender/new" element={
                <RequireRole allow={["SENDER"]}><RequireSenderCompany><NewBooking /></RequireSenderCompany></RequireRole>
              } />
              <Route path="/sender/concierge" element={
                <RequireRole allow={["SENDER","SUPER_ADMIN"]}><RequireSenderCompany allowInactive><ConciergeHome /></RequireSenderCompany></RequireRole>
              } />
              <Route path="/sender/shipments/by-code/:shipmentCode" element={
                <RequireRole allow={["SENDER"]}><RequireSenderCompany allowInactive><ShipmentByCode /></RequireSenderCompany></RequireRole>
              } />
              <Route path="/sender/shipments/:id/revise" element={
                <RequireRole allow={["SENDER"]}><RequireSenderCompany><ReviseBooking /></RequireSenderCompany></RequireRole>
              } />
              <Route path="/sender/shipments/:id" element={
                <RequireRole allow={["SENDER"]}><RequireSenderCompany allowInactive><ShipmentDetail /></RequireSenderCompany></RequireRole>
              } />

              <Route path="/captain" element={
                <RequireRole allow={["CAP","SUPER_ADMIN"]}><CaptainHome /></RequireRole>
              } />

              <Route path="/rider" element={
                <RequireRole allow={["RID"]}><RiderHome /></RequireRole>
              } />
              <Route path="/accounts" element={
                <RequireRole allow={["ACCOUNTS"]}><AccountsHome /></RequireRole>
              } />
              <Route path="/accounts/shipments/:id" element={
                <RequireRole allow={["ACCOUNTS"]}><AccountsWorkspace /></RequireRole>
              } />
              <Route path="/accounts/concierge" element={
                <RequireRole allow={["ACCOUNTS","SUPER_ADMIN"]}><ConciergeStaffPage /></RequireRole>
              } />

              {/* RC-2 — package-label QR deep-link scan landing */}
              <Route path="/scan/pkg/:token" element={
                <RequireRole allow={["CAP","RID"]}><ScanPackage /></RequireRole>
              } />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
